export type FinanceDocumentKind =
    | 'invoice'
    | 'vendor-bill'
    | 'receipt'
    | 'voucher'
    | 'credit-note'
    | 'debit-note';

export type FinancePdfVariant = 'DRAFT' | 'ORIGINAL' | 'LEGACY' | 'REPRINT';
const downloadedVariants = new WeakMap<Blob, FinancePdfVariant>();

export function financePdfVariant(pdf: Blob): FinancePdfVariant | undefined {
    return downloadedVariants.get(pdf);
}

export function rememberFinancePdfVariant(pdf: Blob, variant: string | null): Blob {
    if (variant === 'DRAFT' || variant === 'ORIGINAL' || variant === 'LEGACY' || variant === 'REPRINT') {
        downloadedVariants.set(pdf, variant);
    }
    return pdf;
}

const UNSAFE_FILENAME_CHARACTERS = /[\\/:*?"<>|\u0000-\u001F]+/g;
const WHITESPACE = /\s+/g;
const REPEATED_DASHES = /-{2,}/g;
const LEADING_OR_TRAILING_PUNCTUATION = /^[.-]+|[.-]+$/g;
const PDF_EXTENSION = /(?:\.pdf)+$/i;
const MAXIMUM_BASE_NAME_LENGTH = 180;

export function financeDocumentFileName(
    kind: FinanceDocumentKind,
    documentReference?: string | null,
    documentId?: number | null,
    postingStatus?: string | null
): string {
    const reference = safeBaseName(documentReference);
    const fallback = `${kind}-${documentId ?? 'document'}`;
    const baseName = reference || safeBaseName(fallback) || 'document';
    const status = postingStatus?.toUpperCase();
    const suffix = status === 'LEGACY' || status === 'REPRINT' || status === 'ORIGINAL'
        ? status : status === 'POSTED' ? 'ORIGINAL' : 'DRAFT';
    return `${baseName}-${suffix}.pdf`;
}

export function downloadFinancePdf(
    pdf: Blob,
    kind: FinanceDocumentKind,
    documentReference?: string | null,
    documentId?: number | null,
    postingStatus?: string | null
): void {
    const url = URL.createObjectURL(pdf);
    const anchor = document.createElement('a');
    anchor.href = url;
    const variant = downloadedVariants.get(pdf);
    anchor.download = financeDocumentFileName(kind, documentReference, documentId, variant || postingStatus);
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function safeBaseName(value?: string | null): string {
    return (value ?? '')
        .trim()
        .replace(PDF_EXTENSION, '')
        .replace(UNSAFE_FILENAME_CHARACTERS, '-')
        .replace(WHITESPACE, '-')
        .replace(REPEATED_DASHES, '-')
        .replace(LEADING_OR_TRAILING_PUNCTUATION, '')
        .slice(0, MAXIMUM_BASE_NAME_LENGTH)
        .replace(LEADING_OR_TRAILING_PUNCTUATION, '');
}
